import type { ReactNode } from 'react'

import { cn } from '../cn'

interface PageHeaderProps {
  title: string
  description?: string
  icon?: ReactNode
  actions?: ReactNode
  className?: string
}

/**
 * The title block every page opens with. Ported from legacy `shared/page-header.tsx`.
 *
 * §9.268 — **it wraps.** It was one row that could not, with the actions marked `shrink-0`, so three
 * buttons on `/stock` reached 527 px inside a 390 px phone: the title crushed to a 40 px column with
 * its description one word per line underneath the button overlapping it, and the third button off
 * the edge of the screen entirely. Measured across all 62 sidebar destinations at a phone and a
 * tablet; this component was the cause of both of the two real document overflows.
 *
 * A wrap container lays out identically until something has to wrap, so **nothing moves at a till
 * width** — which is why this is the fix rather than a breakpoint, a hidden-on-mobile rule, or an
 * edit to the thirty-two screens that pass `actions`.
 */
export function PageHeader({ title, description, icon, actions, className }: PageHeaderProps) {
  return (
    <div data-page-header className={cn('flex flex-wrap items-center justify-between gap-4 mb-6', className)}>
      <div className="flex items-center gap-3 min-w-0">
        {icon && (
          <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          {/* §9.268 - addressable. Two `h1`s can be inside `main` at once: the lunch page renders a
            * print template with the restaurant's name in one. */}
          <h1 data-page-title className="text-xl font-bold text-foreground tracking-tight truncate">
            {title}
          </h1>
          {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
        </div>
      </div>
      {/* `shrink-0` stays, so a long title still truncates rather than squashing the buttons - but
        * **`max-w-full` is what makes the wrap work**, and that was measured rather than assumed. A
        * `shrink-0` item takes its max-content width even on a line of its own, so `/stock` still ran
        * to 511 px in a 390 px phone with `flex-wrap` on both containers; the inner row had no
        * constrained width to wrap against. Capping at the header's width gives it one. Where the
        * actions already fit, a cap they are inside changes nothing. */}
      {actions && (
        <div className="flex flex-wrap items-center gap-2 shrink-0 max-w-full">{actions}</div>
      )}
    </div>
  )
}
